/**
 * Business hours: types, helpers, and Zod schema.
 *
 * Storage shape (in `professionals.business_hours` JSONB):
 *
 *   {
 *     mon: [{ open: "09:00", close: "12:00" }, { open: "13:30", close: "18:00" }],
 *     tue: [{ open: "09:00", close: "18:00" }],
 *     ...
 *     sun: [],
 *     by_appointment: false
 *   }
 *
 * Validation rules (all enforced server-side by Zod):
 *   1. Each range satisfies open < close (no overnight ranges).
 *   2. Ranges within a day don't overlap.
 *   3. Max 3 ranges per day.
 *
 * Times are 24h "HH:MM" strings. Locale-aware display formatting is v2 work
 * (see profile-hours-and-address.md). For v1 the format on display matches
 * the storage form.
 */

import { byLocale } from "@/lib/locale";
import { z } from "zod";

export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Day = (typeof DAYS)[number];

export const DAY_LABELS: Record<Day, { full: string; short: string }> = {
  mon: { full: "Monday",    short: "Mon" },
  tue: { full: "Tuesday",   short: "Tue" },
  wed: { full: "Wednesday", short: "Wed" },
  thu: { full: "Thursday",  short: "Thu" },
  fri: { full: "Friday",    short: "Fri" },
  sat: { full: "Saturday",  short: "Sat" },
  sun: { full: "Sunday",    short: "Sun" },
};

// Vietnamese day labels (MACHINE DRAFT — pending native reviewer). Short forms
// follow the common VN convention: T2–T7 (Thứ Hai–Thứ Bảy) + CN (Chủ Nhật).
const DAY_LABELS_VI: Record<Day, { full: string; short: string }> = {
  mon: { full: "Thứ Hai",  short: "T2" },
  tue: { full: "Thứ Ba",   short: "T3" },
  wed: { full: "Thứ Tư",   short: "T4" },
  thu: { full: "Thứ Năm",  short: "T5" },
  fri: { full: "Thứ Sáu",  short: "T6" },
  sat: { full: "Thứ Bảy",  short: "T7" },
  sun: { full: "Chủ Nhật", short: "CN" },
};

const DAY_LABELS_FR: Record<Day, { full: string; short: string }> = {
  mon: { full: "Lundi",    short: "Lun" },
  tue: { full: "Mardi",    short: "Mar" },
  wed: { full: "Mercredi", short: "Mer" },
  thu: { full: "Jeudi",    short: "Jeu" },
  fri: { full: "Vendredi", short: "Ven" },
  sat: { full: "Samedi",   short: "Sam" },
  sun: { full: "Dimanche", short: "Dim" },
};

// Chinese has one natural form; the short one drops 星期 to the common 周.
const DAY_LABELS_ZH_HANS: Record<Day, { full: string; short: string }> = {
  mon: { full: "星期一", short: "周一" },
  tue: { full: "星期二", short: "周二" },
  wed: { full: "星期三", short: "周三" },
  thu: { full: "星期四", short: "周四" },
  fri: { full: "星期五", short: "周五" },
  sat: { full: "星期六", short: "周六" },
  sun: { full: "星期日", short: "周日" },
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const DAY_LABELS_ZH_HANT: Record<Day, { full: string; short: string }> = {
  mon: { full: "星期一", short: "週一" },
  tue: { full: "星期二", short: "週二" },
  wed: { full: "星期三", short: "週三" },
  thu: { full: "星期四", short: "週四" },
  fri: { full: "星期五", short: "週五" },
  sat: { full: "星期六", short: "週六" },
  sun: { full: "星期日", short: "週日" },
};

/** Locale-aware day label (VI, FR and zh machine drafts; other locales → EN). */
export function getDayLabel(day: Day, locale: string): { full: string; short: string } {
  return byLocale(locale, {
    en: DAY_LABELS,
    vi: DAY_LABELS_VI,
    fr: DAY_LABELS_FR,
    "zh-Hans": DAY_LABELS_ZH_HANS,
    "zh-Hant": DAY_LABELS_ZH_HANT,
  })[day];
}

export const MAX_RANGES_PER_DAY = 3;

export interface TimeRange {
  open: string;  // "HH:MM"
  close: string; // "HH:MM"
}

export type DayHours = TimeRange[]; // empty = closed

export interface BusinessHours {
  mon: DayHours;
  tue: DayHours;
  wed: DayHours;
  thu: DayHours;
  fri: DayHours;
  sat: DayHours;
  sun: DayHours;
  by_appointment: boolean;
}

// ── HH:MM helpers ─────────────────────────────────────────────────────────────

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Convert "HH:MM" to minutes since midnight. Returns NaN on bad input. */
export function toMinutes(hhmm: string): number {
  if (!TIME_RE.test(hhmm)) return NaN;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/** Check that ranges within a day are valid: open < close, no overlaps. */
export function validateDayRanges(ranges: TimeRange[]): { ok: true } | { ok: false; reason: string; index: number } {
  if (ranges.length > MAX_RANGES_PER_DAY) {
    return { ok: false, reason: `Max ${MAX_RANGES_PER_DAY} ranges per day.`, index: MAX_RANGES_PER_DAY };
  }
  // Sort copy so overlap detection is order-independent — but report errors
  // against the original index so the UI can highlight the offending row.
  const indexed = ranges.map((r, i) => ({ ...r, originalIndex: i }));
  const sorted = [...indexed].sort((a, b) => toMinutes(a.open) - toMinutes(b.open));

  for (let i = 0; i < sorted.length; i++) {
    const r = sorted[i];
    const o = toMinutes(r.open);
    const c = toMinutes(r.close);
    if (Number.isNaN(o) || Number.isNaN(c)) {
      return { ok: false, reason: "Invalid time format. Use HH:MM (e.g. 09:00).", index: r.originalIndex };
    }
    if (o >= c) {
      return { ok: false, reason: "End time must be after start time.", index: r.originalIndex };
    }
    if (i > 0) {
      const prev = sorted[i - 1];
      const prevClose = toMinutes(prev.close);
      if (o < prevClose) {
        return { ok: false, reason: "Overlaps with another time range.", index: r.originalIndex };
      }
    }
  }
  return { ok: true };
}

// ── Zod ───────────────────────────────────────────────────────────────────────

const timeRangeSchema = z.object({
  open: z.string().regex(TIME_RE, "Use HH:MM format"),
  close: z.string().regex(TIME_RE, "Use HH:MM format"),
});

const dayHoursSchema = z
  .array(timeRangeSchema)
  .max(MAX_RANGES_PER_DAY, `Max ${MAX_RANGES_PER_DAY} ranges per day`)
  .superRefine((ranges, ctx) => {
    const result = validateDayRanges(ranges);
    if (!result.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.reason,
        path: [result.index],
      });
    }
  });

export const businessHoursSchema: z.ZodType<BusinessHours> = z.object({
  mon: dayHoursSchema,
  tue: dayHoursSchema,
  wed: dayHoursSchema,
  thu: dayHoursSchema,
  fri: dayHoursSchema,
  sat: dayHoursSchema,
  sun: dayHoursSchema,
  by_appointment: z.boolean(),
});

// ── Defaults + parsing ────────────────────────────────────────────────────────

export const EMPTY_BUSINESS_HOURS: BusinessHours = {
  mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [],
  by_appointment: false,
};

/**
 * Parse a stored JSONB value into a BusinessHours object.
 * Returns null if the value is null/undefined; returns EMPTY_BUSINESS_HOURS
 * if the value is malformed (defensive — shouldn't happen post-Zod).
 */
export function parseBusinessHours(value: unknown): BusinessHours | null {
  if (value === null || value === undefined) return null;
  const parsed = businessHoursSchema.safeParse(value);
  if (!parsed.success) {
    console.warn("[business-hours] malformed stored value", parsed.error.issues);
    return EMPTY_BUSINESS_HOURS;
  }
  return parsed.data;
}

/** True if the BusinessHours object has any actual schedule (or by-appointment). */
export function hasAnyHours(h: BusinessHours | null): boolean {
  if (!h) return false;
  if (h.by_appointment) return true;
  return DAYS.some((d) => h[d].length > 0);
}

// ── Display helpers (Phase 2 public profile) ──────────────────────────────────

/** Format a single range as "HH:MM – HH:MM". */
export function formatRange(r: TimeRange): string {
  return `${r.open} – ${r.close}`;
}

// Vietnam observes no DST and a fixed +07:00 offset. Computing via offset
// avoids `Intl.DateTimeFormat` locale-string parsing and keeps both day and
// minute calculations identical regardless of the runtime locale.
const VN_OFFSET_MINUTES = 7 * 60;

function vietnamShifted(now: Date): Date {
  return new Date(now.getTime() + VN_OFFSET_MINUTES * 60_000);
}

/** Day-of-week in Asia/Ho_Chi_Minh for the given Date. */
export function getVietnamDay(now: Date): Day {
  // getUTCDay on the offset-shifted timestamp returns the Vietnam weekday
  // index. Sunday = 0 in JS, so we re-key to Monday-first to match DAYS.
  const sundayFirst = vietnamShifted(now).getUTCDay();
  const mondayFirst = (sundayFirst + 6) % 7;
  return DAYS[mondayFirst];
}

/** Minutes-since-midnight in Asia/Ho_Chi_Minh for the given Date. */
export function getVietnamMinutes(now: Date): number {
  const v = vietnamShifted(now);
  return v.getUTCHours() * 60 + v.getUTCMinutes();
}

/** Compare two days by JSON equality of their range arrays. */
function sameDay(a: TimeRange[], b: TimeRange[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((r, i) => r.open === b[i].open && r.close === b[i].close);
}

export interface CollapsedGroup {
  /** Contiguous days sharing the same schedule. */
  days: Day[];
  /** The shared schedule for those days (empty array = closed). */
  ranges: TimeRange[];
}

/**
 * Collapse adjacent days with identical schedules into groups so the public
 * profile can render "Mon – Fri 09:00 – 18:00" instead of five separate rows.
 * Only adjacent runs are collapsed; gaps reset the run.
 */
export function collapseAdjacentDays(h: BusinessHours): CollapsedGroup[] {
  const out: CollapsedGroup[] = [];
  for (const d of DAYS) {
    const last = out[out.length - 1];
    if (last && sameDay(last.ranges, h[d])) {
      last.days.push(d);
    } else {
      out.push({ days: [d], ranges: h[d].map((r) => ({ ...r })) });
    }
  }
  return out;
}

export interface OpenNowState {
  /** True if the current Vietnam time falls inside one of today's ranges. */
  open: boolean;
  /** When `open`, the close time of the current range ("HH:MM"). */
  closesAt?: string;
  /** When closed, the day + open time of the next opening within 7 days. */
  nextOpen?: { day: Day; open: string };
}

/**
 * Compute "Open now / Closed" state given Vietnam-local day + minute and the
 * pro's schedule. By-appointment hours and unset hours are not handled here
 * — render those cases as separate placeholder lines, not a badge.
 */
export function isOpenNow(h: BusinessHours, now: Date): OpenNowState {
  const today = getVietnamDay(now);
  const minutes = getVietnamMinutes(now);

  // Inside a range today?
  for (const r of h[today]) {
    const o = toMinutes(r.open);
    const c = toMinutes(r.close);
    if (Number.isNaN(o) || Number.isNaN(c)) continue;
    if (minutes >= o && minutes < c) {
      return { open: true, closesAt: r.close };
    }
  }

  // Closed — find the next opening within the upcoming 7 days.
  const idx = DAYS.indexOf(today);
  for (let offset = 0; offset < 7; offset++) {
    const day = DAYS[(idx + offset) % 7];
    const ranges = h[day];
    if (ranges.length === 0) continue;
    // Sort by open ascending so we pick the earliest valid range.
    const sorted = [...ranges].sort((a, b) => toMinutes(a.open) - toMinutes(b.open));
    for (const r of sorted) {
      if (offset === 0 && toMinutes(r.open) <= minutes) continue;
      return { open: false, nextOpen: { day, open: r.open } };
    }
  }

  // No openings within the week (every day empty). Still "closed", no nextOpen.
  return { open: false };
}

/** Display label for a collapsed group's day span: "Mon", "Mon – Fri", "Sat – Sun". */
export function formatDaySpan(days: Day[]): string {
  if (days.length === 0) return "";
  if (days.length === 1) return DAY_LABELS[days[0]].short;
  return `${DAY_LABELS[days[0]].short} – ${DAY_LABELS[days[days.length - 1]].short}`;
}
